import { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

import { useQuery } from '@apollo/client/react';

import { useLatestRef } from '@clutter/hooks';

import {
  AvailabilitiesDocument,
  AvailabilitiesQuery,
  AvailabilitiesQueryVariables,
  LongDistancePickupAvailabilitiesDocument,
  MovingQuoteFragment,
  OrderServiceTypeEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
} from '@graphql/platform';

import { normalizeDate } from '@shared/calendar/calendar_utils';

import { clampAvailabilities } from '@utils/availability';

const OFFSET = { days: 1 };
const DEFAULT_INCREMENT = { weeks: 2 };
const INITIAL_OFFSET = { weeks: 4 };

const NO_AVAILABILITY: AvailabilitiesQuery['availabilities'] = [];

type BaseAvailabilityParams = {
  zip: string;
  orderType: OrderTypeEnum;
  orderSubtype: OrderSubtypeEnum;
  startDate?: DateTime;
  tillDate?: DateTime;
  skip?: boolean;
  /** Clamp availabilities to the provided dates and read from the cache only.  */
  clamp?: boolean;
};

type StorageAvailabilityParams = {
  expectedPlanID: string | undefined; // Included for better type safety
  serviceType: OrderServiceTypeEnum | undefined; // Included for better type safety
};

type MovingAvailabilityParams = {
  movers?: number;
  movingQuote?: MovingQuoteFragment;
  expectedPlanID?: string;
};

const useAvailabilities = ({
  zip,
  orderType,
  orderSubtype,
  expectedPlanID,
  startDate,
  tillDate,
  skip,
  movers,
  serviceType,
  clamp,
  movingQuote,
}: BaseAvailabilityParams &
  Partial<StorageAvailabilityParams & MovingAvailabilityParams>) => {
  const from =
    startDate ||
    DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const till = tillDate || from.plus(INITIAL_OFFSET);
  const [cursor, setCursor] = useState<DateTime>(till);
  const tillRef = useLatestRef(till);

  const buildInput = (
    inputFrom: DateTime = from,
    inputTill: DateTime = till,
  ): AvailabilitiesQueryVariables['input'] => {
    return {
      from: inputFrom.toISO(),
      till: inputTill.toISO(),
      order: {
        address: {
          zip,
          street: '',
          city: '',
          state: '',
        },
        type: orderType,
        subtype: orderSubtype,
        expectedPlanID,
        movers,
        serviceType,
      },
      movingQuoteSignedID: movingQuote?.signedID,
    };
  };

  const longDistance = !!movingQuote?.longDistance;

  useEffect(() => {
    // If any input parameter changes that could impact availability, we reset
    // the paged availability to make sure it can be re-fetched. Alternately, we
    // could imperatively re-fetch from till -> cursor but this isn't a common
    // occurance.
    setCursor(tillRef.current);
  }, [
    zip,
    orderType,
    orderSubtype,
    expectedPlanID,
    movers,
    serviceType,
    tillRef,
    longDistance,
  ]);

  const standardQueryResult = useQuery(AvailabilitiesDocument, {
    variables: { input: buildInput() },
    fetchPolicy: clamp ? 'cache-only' : undefined,
    skip: skip || longDistance,
  });

  const longDistanceQueryResult = useQuery(
    LongDistancePickupAvailabilitiesDocument,
    {
      variables: {
        from: from.toISO(),
        till: till.toISO(),
        movingQuoteSignedID: movingQuote?.signedID || '',
        zip,
      },
      fetchPolicy: clamp ? 'cache-only' : undefined,
      skip: skip || !longDistance,
    },
  );

  const { data, loading } = longDistance
    ? longDistanceQueryResult
    : standardQueryResult;

  const availabilities = data ? data.availabilities : NO_AVAILABILITY;
  const clampedAvailabilities =
    clamp && startDate && tillDate
      ? clampAvailabilities(availabilities, { from: startDate, till: tillDate })
      : availabilities;

  const normalizedDate = normalizeDate(from);
  // Use normalized date if in same month, otherwise fallback to first day of original month
  // to prevent crossing month boundaries when going back a week
  const resolvedDate =
    normalizedDate.month === from.month
      ? normalizedDate
      : from.plus({ days: 1 - from.day });

  const fetchMore = () => {
    const current = cursor;
    setCursor(current.plus(DEFAULT_INCREMENT));

    if (skip) {
      return;
    }

    const nextFrom = current.plus(OFFSET);
    const nextTill = current.plus(DEFAULT_INCREMENT);

    if (longDistance) {
      longDistanceQueryResult.fetchMore({
        variables: {
          zip,
          from: nextFrom.toISO(),
          till: nextTill.toISO(),
          movingQuoteSignedID: movingQuote?.signedID || '',
        },
      });
    } else {
      standardQueryResult.fetchMore({
        variables: {
          input: buildInput(nextFrom, nextTill),
        },
      });
    }
  };

  return {
    from: resolvedDate,
    till: cursor,
    availabilities: clampedAvailabilities,
    fetchMore,
    loading,
    fetched: !!data,
  };
};

export const useStorageAvailabilities = (
  params: BaseAvailabilityParams & StorageAvailabilityParams,
) => useAvailabilities(params);
